/**
 * D-EZ-96 — THE NAMES ON THE CAR.
 *
 * Sagliano prices a sponsorship and never names it. His own contingency awards
 * (SAG/24) do: "Poler Bare Dancing Promotions — Drivers Can Get Poles too!",
 * "Eleventh Hour Mortuary", "Wallop Whiskey", "Atomic Number 31 Metals". That
 * is the house tone — small-town businesses with a pun and a straight face —
 * and every name below is written in it.
 *
 * ⚠ NO REAL BRANDS. Every name here is invented, and the generator only ever
 * combines invented parts. A test checks the pools against a list of real
 * sponsor marks so that a later edit cannot slip one in.
 *
 * NO DICE. A name is a pure hash of the contract's identity (league, driver,
 * slot, the race it was signed at), so naming a sponsor can never move a single
 * Megabuck of the money rolls `raceFinance.ts` makes in its fixed order.
 */

import type { DriverFinanceState, SponsorContract } from './raceFinance.ts';

/** Whole names, written to stand on their own on a hood. */
export const SPONSOR_NAMES: readonly string[] = [
  'Lug Nut Lumber & Supply',
  'Checkered Past Pawn & Loan',
  'Caution Flag Insurance',
  'Wide Open Throttle Coffee',
  'Pit Road Pancake House',
  'Back Marker Bail Bonds',
  'Draft Horse Feed & Seed',
  'Green-White-Checkered Laundromat',
  'Victory Lane Septic Service',
  'Tight Turn Tire & Muffler',
  'Splash-and-Go Car Wash',
  'Push Rod Pest Control',
  'Short Track Shortening Co.',
  'Restart Realty',
  'Pace Car Pie Company',
  'Lapped Traffic Driving School',
  'Rumble Strip Mattress Barn',
  'Drafting Table Architects',
  'Last Lap Liquidators',
  'Overdrive Orthodontics',
  'Big One Demolition',
  'Loose Wheel Rodeo Supply',
  'Hot Lap Hot Sauce',
  'Crew Chief Chili Parlor',
  'Fuel Mileage Diet Plans',
  'Black Flag Dry Cleaners',
  'Trackbar Tavern',
  'Spotter Optical',
  'Fender Bender Body & Paint',
  'Tire Smoke Barbecue',
  'Bump-and-Run Bowling Lanes',
  'Fast Pit Fried Chicken',
  'Lucky Dog Kennels',
  'Wave-Around Window Washing',
  'Pole Cat Plumbing',
  'Scuff Tire Shoe Repair',
  'Burnout Hair Salon',
  'Photo Finish Portrait Studio',
  'Yellow Line Paint & Body',
  'Turn Four Tax Preparation',
  'Catch Can Canning Supply',
  'Wedge Adjustment Chiropractic',
  'Tear-Off Tanning Salon',
  'Infield Inflatables',
  'Grandstand Gutters',
  'Brake Fade Funeral Home',
  'Stage Break Pizza',
  'Overtime Overalls',
  'Points Leader Pickles',
  'Round Robin Roofing',
  'Backstretch Bait & Tackle',
  'Dogleg Dog Grooming',
  'Long Run Pantyhose',
  'Sticker Tire Sign Shop',
  'Pit Stall Portable Toilets',
  'Wrecker Service Wedding Chapel',
  'Swing Arm Square Dance Hall',
  'Gear Ratio Guitar Repair',
  'Track Bar Taxidermy',
  'Free Pass Pharmacy',
];

/** Opening words for generated names, in the same voice. */
const FIRST_WORDS: readonly string[] = [
  'Full Throttle', 'Lug Nut', 'Pit Lane', 'Caution', 'Overdrive', 'Short Track', 'Restart', 'Back Stretch',
  'Checkered', 'Pole Cat', 'Push Rod', 'Splash-and-Go', 'Fender', 'Tailpipe', 'Turn Three', 'Hot Lap',
  'Victory', 'Burnout', 'Drafting', 'Slingshot', 'Wedge', 'Rumble Strip', 'Green Flag', 'Lucky Dog',
];

/** Trades, the shop half of a generated name. */
const TRADES: readonly string[] = [
  'Bait & Tackle', 'Funeral Home', 'Dental Care', 'Feed & Seed', 'Septic Service', 'Bail Bonds', 'Pawn & Loan',
  'Barbecue', 'Insurance Agency', 'Car Wash', 'Chiropractic', 'Hardware', 'Pancake Parlor', 'Tax Service',
  'Mattress Outlet', 'Roofing', 'Pest Control', 'Driving School', 'Taxidermy', 'Bowling Lanes', 'Laundromat',
  'Hot Sauce', 'Lumber Yard', 'Bakery', 'Storage Units', 'Salvage', 'Optometry', 'Gun & Pawn', 'Pickles',
];

export type SponsorSlot =
  | 'Primary'
  | 'Major #1'
  | 'Major #2'
  | 'Associate #1'
  | 'Associate #2'
  | 'Associate #3'
  | 'Special';

/** The slots in the order Howard's sheet lists them (`Season Points 3.xlsx`, team block). */
export const SPONSOR_SLOTS: readonly SponsorSlot[] = [
  'Primary',
  'Major #1',
  'Major #2',
  'Associate #1',
  'Associate #2',
  'Associate #3',
  'Special',
];

function hash(text: string): number {
  // FNV-1a, then a murmur finaliser — stable across runtimes, no dice touched.
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * A sponsor's name, from its identity. `key` should say who and when — the
 * league, the driver, the tier and the race it was signed at. `avoid` keeps a
 * driver from carrying the same name twice on one car.
 */
export function sponsorNameFor(key: string, avoid: ReadonlySet<string> = new Set()): string {
  const h = hash(key);
  // Two in three come from the written list; the rest are composed, which keeps
  // a long career from running out of fresh names.
  const candidates: string[] = [];
  if (h % 3 !== 0) {
    for (let i = 0; i < SPONSOR_NAMES.length; i++) candidates.push(SPONSOR_NAMES[(h + i * 7) % SPONSOR_NAMES.length]);
  }
  for (let i = 0; i < 40; i++) {
    const a = FIRST_WORDS[(h + i * 13) % FIRST_WORDS.length];
    const b = TRADES[((h >>> 8) + i * 5) % TRADES.length];
    candidates.push(`${a} ${b}`);
  }
  for (const name of candidates) if (!avoid.has(name)) return name;
  return `${candidates[0]} No. ${(h % 90) + 10}`;
}

/** Every name the generator can produce from its two word pools — for the brand audit test. */
export function sponsorVocabulary(): { names: readonly string[]; firstWords: readonly string[]; trades: readonly string[] } {
  return { names: SPONSOR_NAMES, firstWords: FIRST_WORDS, trades: TRADES };
}

export interface NamedSponsor {
  slot: SponsorSlot;
  name: string;
  tier: SponsorContract['tier'];
  yearlyMb: number;
  /** Seasons this sponsor has been on the car, this one included. */
  seasons: number;
  /** Race it was signed at this season; 0 = on the car from the start of the year. */
  signedRace: number;
}

/**
 * A driver's sponsors, laid out in the sheet's slots. Contracts without a name
 * (an older saved state) are shown as "Unnamed sponsor" rather than skipped.
 */
export function sponsorSlotsFor(dfs: DriverFinanceState): NamedSponsor[] {
  const out: NamedSponsor[] = [];
  const add = (slot: SponsorSlot, c: SponsorContract): void => {
    out.push({
      slot,
      name: c.sponsorName ?? 'Unnamed sponsor',
      tier: c.tier,
      yearlyMb: c.yearlyMb,
      seasons: c.seasonsHeld ?? 1,
      signedRace: c.signedRace,
    });
  };
  if (dfs.primary) add('Primary', dfs.primary);
  dfs.major.slice(0, 2).forEach((c, i) => add(i === 0 ? 'Major #1' : 'Major #2', c));
  dfs.associate.slice(0, 3).forEach((c, i) => add((['Associate #1', 'Associate #2', 'Associate #3'] as const)[i], c));
  return out;
}
